
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import {
  Heart, BookOpen, Mountain, Film, Users, Globe, Code,
  GraduationCap, MapPin, Utensils, Calendar, LucideIcon,
  CupSoda, Award, Lightbulb, Scale, Coffee
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface PersonalItemProps {
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
  delay?: number;
  className?: string;
}

const PersonalItem = ({ icon, title, content, delay = 0, className }: PersonalItemProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Card
      ref={itemRef}
      className={cn(
        "bg-card/40 backdrop-blur-sm hover:bg-card/60 transition-all duration-300 hover:shadow-md border-primary/10 hover:border-primary/20 overflow-hidden hover:-translate-y-1",
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-8",
        className
      )}
      style={{
        transitionDelay: `${delay}ms`,
      }}
    >
      <CardContent className="p-6">
        <div className="flex gap-4">
          <div className="shrink-0 bg-primary/10 rounded-full p-3 h-14 w-14 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-2">{title}</h3>
            <div className="text-muted-foreground text-sm space-y-2">
              {content}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PersonalValueCard = ({ icon, title, description, delay }: {
  icon: React.ReactNode,
  title: string,
  description: string,
  delay: number
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={itemRef}
      className={cn(
        "flex flex-col items-center text-center p-4 transition-all duration-300",
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-6"
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="bg-primary/10 rounded-full p-4 mb-3 animate-pulse-slow">
        {icon}
      </div>
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
};

const PersonalSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const coreValues = [
    {
      icon: <Heart className="h-6 w-6 text-pink-400" />,
      title: "Authenticity",
      description: "Being genuine in all interactions, staying true to myself regardless of the setting."
    },
    {
      icon: <GraduationCap className="h-6 w-6 text-blue-400" />,
      title: "Growth Mindset",
      description: "Viewing every challenge as an opportunity to learn and improve."
    },
    {
      icon: <Scale className="h-6 w-6 text-amber-400" />,
      title: "Responsibility",
      description: "Taking commitments seriously, whether supporting family or delivering on projects."
    },
    {
      icon: <Coffee className="h-6 w-6 text-green-400" />,
      title: "Work-life Harmony",
      description: "Balancing productivity and personal joy for a fulfilling life."
    }
  ];

  const personalItems = [
    {
      icon: <BookOpen className="h-8 w-8 text-blue-400" />,
      title: "Lifelong Learner",
      content: (
        <>
          <p>I'm a voracious reader, drawn to books that help me grow, whether it's self-help, philosophy, or deep dives into software architecture and system design.</p>
          <p><strong>Currently reading:</strong> Life's Amazing Secrets</p>
          <p>I also enjoy spiritual literature that helps me reflect, reset, and reconnect with my values.</p>
        </>
      )
    },
    {
      icon: <Mountain className="h-8 w-8 text-green-400" />,
      title: "Off The Grid",
      content: (
        <>
          <p>When I'm not coding, I love exploring the great outdoors, from weekend hikes to spontaneous bike rides around the city.</p>
          <p>I enjoy traveling to new places, trying local cuisines, and experiencing different cultures, it helps me expand my worldview.</p>
          <p>I'm always up for a chai adda with close friends, especially when it leads to laughter, deep talks, or a new travel plan.</p>
        </>
      )
    },
    {
      icon: <Film className="h-8 w-8 text-red-400" />,
      title: "Pop Culture Fanboy",
      content: (
        <>
          <p>Big-time follower of the Marvel Cinematic Universe (Endgame still hits hard).</p>
          <p>Harry Potter nerd, you could say my Hogwarts letter just got lost in the mail.</p>
          <p>I often draw inspiration from the storytelling and character arcs in films and shows.</p>
        </>
      )
    },
    {
      icon: <Users className="h-8 w-8 text-amber-400" />,
      title: "Family First",
      content: (
        <>
          <p>I come from a middle-class Indian family and deeply value the emotional strength and cultural roots I've grown up with.</p>
          <p>I'm currently the sole earner, and supporting my family is one of my proudest roles.</p>
          <p>This responsibility has shaped my discipline, empathy, and drive to do well, not just for me, but for those who count on me.</p>
        </>
      )
    },
    {
      icon: <Code className="h-8 w-8 text-cyan-400" />,
      title: "Tech + Community",
      content: (
        <>
          <p>I love being part of the tech community, from attending meetups and webinars to contributing and helping peers learn.</p>
          <p>I believe knowledge grows when shared, and I'm always up for a good discussion on clean code, architecture, or industry trends.</p>
          <p>Currently brushing up on backend skills to become a better-rounded developer.</p>
        </>
      )
    }
  ];

  return (
    <section id="personal" className="bg-background/80 py-20 relative" ref={sectionRef}>
      <div className="personal-vector personal-vector-1"></div>
      <div className="personal-vector personal-vector-2"></div>

      <div className="section-container relative z-10">
        <div className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
          <p className="heading-highlight mb-2 animate-pulse-slow text-center">Beyond The Code</p>
          <h2 className="text-3xl font-bold mb-4 text-center">Who I Am Outside Work</h2>

          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto text-center">
            While coding is my profession, there's much more to me than just lines of code.
            Here's a glimpse into who I am when I step away from the keyboard.
          </p>

          <div className="mb-12 mt-8">
            <h3 className="text-xl font-semibold mb-6 text-center">🌱 Core Values</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {coreValues.map((value, index) => (
                <PersonalValueCard
                  key={value.title}
                  icon={value.icon}
                  title={value.title}
                  description={value.description}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {personalItems.map((item, index) => (
              <PersonalItem
                key={item.title}
                icon={item.icon}
                title={item.title}
                content={item.content}
                delay={index * 150}
              />
            ))}
          </div>

          <div className={`mt-12 text-center transition-all duration-500 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
            <p className="text-primary/80 italic">
              "Ultimately, I believe that the best developers are shaped not only by what they build, but also by the lives they live.
              That's what I bring, code with heart, backed by values, curiosity, and a commitment to growth."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalSection;
